import React from 'react';

import { ActionIcon, createStyles, Title, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconCopy, IconGripVertical, IconTrash } from '@tabler/icons-react';

interface SectionHeaderProps {
  sectionTitle: string;
  sectionOpened: boolean;
  onToggle: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  canDelete: boolean;
  sectionId: string;
}

const useStyles = createStyles(theme => ({
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    cursor: 'pointer',
    flex: 1,
  },
  dragHandle: {
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    '&:active': { cursor: 'grabbing' },
  },
  sectionActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
}));

const SectionHeader = ({
  sectionTitle,
  sectionOpened,
  onToggle,
  onDuplicate,
  onDelete,
  canDelete,
  sectionId,
}: SectionHeaderProps): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();

  return (
    <div className={classes.sectionHeader}>
      <div className={classes.sectionTitle} onClick={onToggle}>
        <div className={classes.dragHandle} onMouseDown={e => e.stopPropagation()}>
          <IconGripVertical size={20} color={theme.colors.gray[6]} />
        </div>
        <Title order={4}>{sectionTitle}</Title>
      </div>
      <div className={classes.sectionActions}>
        <ActionIcon
          variant='subtle'
          size='md'
          onClick={e => {
            e.stopPropagation();
            onDuplicate();
          }}
          data-cy={`duplicateSection-${sectionId}`}
        >
          <IconCopy size={16} />
        </ActionIcon>
        {canDelete && (
          <ActionIcon
            variant='subtle'
            color='red'
            size='md'
            onClick={e => {
              e.stopPropagation();
              onDelete();
            }}
            data-cy={`deleteSection-${sectionId}`}
          >
            <IconTrash size={16} />
          </ActionIcon>
        )}
        <ActionIcon
          variant='subtle'
          size='md'
          onClick={e => {
            e.stopPropagation();
            onToggle();
          }}
          sx={{
            transform: sectionOpened ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease',
          }}
        >
          <IconChevronDown size={16} />
        </ActionIcon>
      </div>
    </div>
  );
};

export default SectionHeader;
